import React, { useEffect, useRef, useState, useContext } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom'; // Outlet renders the matched child route element
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import ThemeSidebar from './ThemeSidebar';
import AmazonSyncBanner from './AmazonSyncBanner';
import styles from './DashboardLayout.module.css';
import useReferralBonus from '../../hooks/useReferralBonus';
import ReferralBonusModal from '../UI/ReferralBonusModal';
import CreditsExhaustedModal from '../UI/CreditsExhaustedModal';
import AuthContext from '../../context/AuthContext';
import { useFormStep } from '../../context/FormStepContext';
import { LOW_CREDITS_EVENT } from '../../utils/lowCreditsModal';
import { clearAmazonStatusCache } from '../../hooks/useAmazonStatus';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Show the low-credits popup when the balance drops below this threshold.
const LOW_CREDITS_THRESHOLD = 50;

const getAuthToken = () => {
  return localStorage.getItem('authToken') ||
         localStorage.getItem('idToken') ||
         localStorage.getItem('refreshToken') ||
         localStorage.getItem('token') ||
         sessionStorage.getItem('token');
};

const DashboardLayout = () => {
  // Use the referral bonus hook
  const { showBonusModal, closeBonusModal, bonusDetails } = useReferralBonus();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formStep, listingMode, showVariantsTab, isThemesPanelOpen, setIsThemesPanelOpen, showResultPopup, showBothSidebars, setShowBothSidebars, isFontsPanelOpen, setIsFontsPanelOpen } = useFormStep();
  const finalizeCalledRef = useRef(false);

  // Low-credits popup: surfaces the current balance whenever it's below the
  // threshold. Keyed to the balance value (not a single boolean) so every new low
  // value shows once — a lone boolean flag would let the first low popup suppress
  // all later ones (e.g. seeing "0" then never seeing "1"). Zero always re-shows.
  const authCtx = useContext(AuthContext);
  const [showLowCredits, setShowLowCredits] = useState(false);
  // Optional action to run when "Buy Credits" is clicked (e.g. save a draft).
  const [onBuyCredits, setOnBuyCredits] = useState(null);
  // True when the popup was triggered by a feature attempted without enough credits
  // (vs. the passive dashboard balance warning) — switches the modal to "insufficient".
  const [lowCreditsInsufficient, setLowCreditsInsufficient] = useState(false);

  useEffect(() => {
    const credits = authCtx?.user?.credits;
    if (typeof credits !== 'number') return;

    const SHOWN_FOR_KEY = 'lowCreditsShownFor';

    if (credits >= LOW_CREDITS_THRESHOLD) {
      // Balance healthy again — clear so a future dip re-shows.
      sessionStorage.removeItem(SHOWN_FOR_KEY);
      return;
    }

    // Show for zero on every load (hard block), and once per distinct low value
    // otherwise, so a changed balance is always reflected without nagging on repeats.
    if (credits === 0 || sessionStorage.getItem(SHOWN_FOR_KEY) !== String(credits)) {
      sessionStorage.setItem(SHOWN_FOR_KEY, String(credits));
      setOnBuyCredits(null);
      setLowCreditsInsufficient(false);
      setShowLowCredits(true);
    }
  }, [authCtx?.user?.credits]);

  // Show the popup when a feature reports a credit-blocked action (e.g. a
  // NO_CREDITS API response) via triggerLowCreditsModal(). An optional
  // onBuyCredits callback (e.g. save draft) runs before navigating to plans.
  useEffect(() => {
    const handler = (e) => {
      const cb = e?.detail?.onBuyCredits;
      setOnBuyCredits(() => (typeof cb === 'function' ? cb : null));
      setLowCreditsInsufficient(true);
      setShowLowCredits(true);
    };
    window.addEventListener(LOW_CREDITS_EVENT, handler);
    return () => window.removeEventListener(LOW_CREDITS_EVENT, handler);
  }, []);

  // One-time Amazon finalize: triggered only when redirected back with ?amazon_connected=true
  useEffect(() => {
    if (searchParams.get('amazon_connected') && !finalizeCalledRef.current) {
      finalizeCalledRef.current = true;

      // Just connected — drop any stale cached "not connected" status so the
      // UI reflects the new connection instead of staying on "Connect Amazon".
      clearAmazonStatusCache();

      // Clean up the URL parameter immediately
      searchParams.delete('amazon_connected');
      setSearchParams(searchParams, { replace: true });

      // Call finalize API once in the background
      const token = getAuthToken();
      if (token) {
        fetch(`${API_URL}/auth/amazon/finalize`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }).catch(() => {});
      }
    }
  }, [searchParams, setSearchParams]);
  
  // Check if we're on the infographics tab in single or bulk listing generator
  // Infographics step depends on whether Variants tab is enabled.
  // - Without Variants: Infographics is step 4 (new) / step 5 (existing)
  // - With Variants:    Variants is step 4 (new) / step 5 (existing), Infographics shifts to step 5 (new) / step 6 (existing)
  // Don't show ThemeSidebar when results are showing
  const infographicsStep = showVariantsTab
    ? (listingMode === 'new' ? 5 : 6)
    : (listingMode === 'new' ? 4 : 5);

  const isSingleListingPage = location.pathname.includes('/dashboard/generate/single');
  const isBulkListingPage = location.pathname.includes('/dashboard/generate/bulk');

  const isInfographicsTab =
    !showResultPopup &&
    (isSingleListingPage || isBulkListingPage) &&
    formStep === infographicsStep;
  
  // Close themes panel when results are showing
  useEffect(() => {
    if (showResultPopup && isThemesPanelOpen) {
      setIsThemesPanelOpen(false);
    }
  }, [showResultPopup, isThemesPanelOpen, setIsThemesPanelOpen]);

  // Close fonts panel when results are showing
  useEffect(() => {
    if (showResultPopup && isFontsPanelOpen) {
      setIsFontsPanelOpen(false);
    }
  }, [showResultPopup, isFontsPanelOpen, setIsFontsPanelOpen]);

  // Reset showBothSidebars when navigating away from infographics tab
  useEffect(() => {
    if (!isInfographicsTab && showBothSidebars) {
      setShowBothSidebars(false);
    }
  }, [isInfographicsTab, showBothSidebars, setShowBothSidebars]);

  // Close themes panel when navigating away from infographics tab
  useEffect(() => {
    if (!isInfographicsTab && isThemesPanelOpen) {
      setIsThemesPanelOpen(false);
    }
  }, [isInfographicsTab, isThemesPanelOpen, setIsThemesPanelOpen]);

  // Close fonts panel when navigating away from infographics tab
  useEffect(() => {
    if (!isInfographicsTab && isFontsPanelOpen) {
      setIsFontsPanelOpen(false);
    }
  }, [isInfographicsTab, isFontsPanelOpen, setIsFontsPanelOpen]);
  
  const handleThemesToggle = (isOpen) => {
    setIsThemesPanelOpen(isOpen);
  };
  
  return (
    <div className={styles.dashboardLayout}>
      {showBothSidebars && isInfographicsTab ? (
        <>
          <Sidebar showBothSidebars={true} />
          <ThemeSidebar onThemesToggle={handleThemesToggle} isBulk={isBulkListingPage} />
        </>
      ) : isInfographicsTab ? (
        <ThemeSidebar onThemesToggle={handleThemesToggle} isBulk={isBulkListingPage} />
      ) : (
      <Sidebar />
      )}
      <div className={`${styles.mainContent} ${(isThemesPanelOpen || isFontsPanelOpen) && isInfographicsTab ? styles.mainContentWithPanel : ''} ${showBothSidebars && isInfographicsTab ? styles.mainContentWithBothSidebars : ''}`}>
        <Navbar />
        <AmazonSyncBanner />
        <div className={styles.pageContent}>
          <Outlet /> {/* Child routes defined in App.js will render here */}
        </div>
      </div>

      {/* Referral Bonus Modal */}
      <ReferralBonusModal
        show={showBonusModal}
        onClose={closeBonusModal}
        creditsAdded={bonusDetails.creditsAdded}
        tokensAdded={bonusDetails.tokensAdded}
      />

      {/* Low-credits popup (< 50 credits) */}
      <CreditsExhaustedModal
        show={showLowCredits}
        onClose={() => { setShowLowCredits(false); setOnBuyCredits(null); setLowCreditsInsufficient(false); }}
        onBuyCredits={onBuyCredits}
        credits={authCtx?.user?.credits}
        insufficient={lowCreditsInsufficient}
      />
    </div>
  );
};

export default DashboardLayout;